import type { EditHackathonData } from '../components/Hackathon/HackathonEditor';
import type { HackathonPayload } from '../services/hackathon.service';

/** An empty create form. */
export const blankHackathon: EditHackathonData = {
    name: 'Untitled Hackathon',
    description: '',
    startDate: null,
    endDate: null,
    categories: [],
    categoryMeta: [],
    ideaSubmissionDeadline: null,
    inviteCode: '',
    domain: '',
    maxTeamSize: 4,
    minTeamSize: 2,
    participantScores: false,
    blindJudgeScores: false,
    anonymousJudgeScores: false,
    ideaTemplate: '',
    preferredAuth: '',
    branding: {},
    organization: ''
};

/** The next one in a series: "Hack Week 3" -> "Hack Week 4"; otherwise "<name> (next)", so it's obviously not the old event. */
export const nextName = (name: string) => {
    const numbered = name.match(/^(.*?)(\d+)(\D*)$/);
    return numbered ? `${numbered[1]}${Number(numbered[2]) + 1}${numbered[3]}` : `${name} (next)`;
};

/**
 * A past event's setup for a new one: categories, description, team sizes, access (invite code, domain, sign-in),
 * scoring, pitch template and branding. Not its dates, ideas or people. Its organization only if the user can still
 * use it (`usableOrganizations`), or creating would fail. Categories get new ids when the new event is saved.
 */
export const copyOf = (past: HackathonPayload, usableOrganizations: string[]): EditHackathonData => ({
    ...blankHackathon,
    name: nextName(past.name),
    description: past.description || '',
    categories: [...past.categories],
    categoryMeta: past.categoryMeta.map(meta => ({ judgeOnly: !!meta.judgeOnly, description: meta.description || '' })),
    inviteCode: past.inviteCode || '',
    domain: past.domain || '',
    preferredAuth: past.preferredAuth || '',
    minTeamSize: past.minTeamSize || blankHackathon.minTeamSize,
    maxTeamSize: past.maxTeamSize || blankHackathon.maxTeamSize,
    participantScores: !!past.participantScores,
    blindJudgeScores: !!past.blindJudgeScores,
    anonymousJudgeScores: !!past.anonymousJudgeScores,
    ideaTemplate: past.ideaTemplate || '',
    branding: past.branding || {},
    organization: past.organization?._id && usableOrganizations.includes(past.organization._id) ? past.organization._id : ''
});

/**
 * What an organization's access rule puts into the form: its domain and sign-in, but only if it has a rule (an
 * organization without one must not wipe what the organizer typed or copied).
 */
export const orgAccessPatch = (org?: { access?: { domain: string, preferredAuth: string } } | null): Partial<EditHackathonData> =>
    org?.access && (org.access.domain || org.access.preferredAuth)
        ? { domain: org.access.domain, preferredAuth: org.access.preferredAuth }
        : {};
